import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { merge } from 'lodash';
import { lowerCase, snakeCase } from 'lodash';

import { EntityFilter, Filter, JoinOperator, FilterStoreState } from './filterStore';
interface GlobalPropertiesFilter extends EntityFilter {
  filter: {
    pwsId: Filter[];
    zone: Filter[];
  };
  setPwsIdFilter: (pwsId: Filter[]) => void;
  setZoneFilter: (zone: Filter[]) => void;
}

export interface GlobalFilterSlice
  extends Omit<
    FilterStoreState,
    'getQueryParamRepresentationByEntity' | 'getBuildDataFilterFunctionByEntity'
  > {
  properties: GlobalPropertiesFilter;
}

const buildGlobalDataFilterFunction = (
  get,
  entity: keyof GlobalFilterSlice
): ((d: any) => Boolean) => {
  const currentFilterState = get();
  if (!(entity in currentFilterState)) return (d) => true;

  const entityFilter = currentFilterState[entity].filter;
  const useAndJoin = currentFilterState[entity].joinOperator === 'and';

  const filterFunc = (d) => {
    let count = 0;

    let include = useAndJoin ? true : false;
    const keys = Object.keys(entityFilter);
    for (const field of keys) {
      const fieldFilter = entityFilter[field];
      const modifiedField = field == 'zone' ? 'zone_name' : field;
      if (useAndJoin) {
        if (fieldFilter.length === 0) {
          continue;
        }
        for (const filter of fieldFilter) {
          count++;
          switch (filter.operator) {
            case 'eq':
              if (useAndJoin && d.properties[snakeCase(modifiedField)] != filter.value)
                include = false;
              else if (!useAndJoin && d.properties[snakeCase(modifiedField)] == filter.value)
                include = true;
              break;
            case 'neq':
              if (useAndJoin && d.properties[snakeCase(modifiedField)] == filter.value)
                include = false;
              else if (!useAndJoin && d.properties[snakeCase(modifiedField)] != filter.value)
                include = true;
              break;
            case 'lt':
              if (useAndJoin && d.properties[snakeCase(modifiedField)] >= filter.value)
                include = false;
              else if (!useAndJoin && d.properties[snakeCase(modifiedField)] < filter.value)
                include = true;
              break;
            case 'lte':
              if (useAndJoin && d.properties[snakeCase(modifiedField)] > filter.value)
                include = false;
              else if (!useAndJoin && d.properties[snakeCase(modifiedField)] <= filter.value)
                include = true;
              break;
            case 'gt':
              if (useAndJoin && d.properties[snakeCase(modifiedField)] <= filter.value)
                include = false;
              else if (!useAndJoin && d.properties[snakeCase(modifiedField)] > filter.value)
                include = true;
              break;
            case 'gte':
              if (useAndJoin && d.properties[snakeCase(modifiedField)] < filter.value)
                include = false;
              else if (!useAndJoin && d.properties[snakeCase(modifiedField)] >= filter.value)
                include = true;
              break;
            case 'like':
              if (useAndJoin && d.properties[snakeCase(modifiedField)].indexOf(filter.value) < 0)
                include = false;
              else if (
                !useAndJoin &&
                d.properties[snakeCase(modifiedField)].indexOf(filter.value) >= 0
              )
                include = true;
              break;
            case 'ilike':
              if (
                useAndJoin &&
                lowerCase(d.properties[snakeCase(modifiedField)]).indexOf(lowerCase(filter.value)) <
                  0
              )
                include = false;
              else if (
                !useAndJoin &&
                lowerCase(d.properties[snakeCase(modifiedField)]).indexOf(
                  lowerCase(filter.value)
                ) >= 0
              )
                include = true;
              break;
            case 'any':
            default:
              break;
          }
        }
      }
    }
    return include || count === 0;
  };
  return filterFunc;
};

const globalQueryParamRepresentationHelper = (get, entity: keyof GlobalFilterSlice) => {
  /*
  For the global filter we cannot use the helper `getQueryParamRepresentationByEntityHelper` in filterStore due to a
  circular dependency. In particular in order to respect global filters the function `getQueryParamRepresentationByEntityHelper`
  depends on the globalFilterStore. In the future we can probably refactor the function to get around this but for now lets
  keep it simple.
  */
  const queryParams = {};
  const currentFilterState = get();
  if (!(entity in currentFilterState)) return queryParams;
  const entityFilter = currentFilterState[entity].filter;
  Object.keys(entityFilter).forEach((field) => {
    const fieldFilter = entityFilter[field];
    if (fieldFilter.length === 0) return;
    const mapping = fieldFilter.map((filter: Filter) => `${filter.operator}:${filter.value}`);
    queryParams[`filter[${field}]`] = mapping.length === 1 ? mapping[0] : mapping;
  });
  return queryParams;
};

const initalState = (set, get): GlobalFilterSlice => ({
  properties: {
    filter: {
      pwsId: [] as Filter[],
      zone: [] as Filter[],
    },
    joinOperator: 'and' as JoinOperator, // Defaults to and
    setJoinOperator: (joinOperator: JoinOperator) =>
      set((state) => {
        return {
          properties: { ...state.properties, joinOperator },
        };
      }),
    setPwsIdFilter: (pwsId: Filter[]) =>
      set((state) => ({
        properties: {
          ...state.properties,
          filter: {
            ...state.properties.filter,
            pwsId,
          },
        },
      })),
    setZoneFilter: (zone: Filter[]) =>
      set((state) => ({
        properties: {
          ...state.properties,
          filter: {
            ...state.properties.filter,
            zone,
          },
        },
      })),
    getQueryParamRepresentation: () => {
      return globalQueryParamRepresentationHelper(get, 'properties');
    },
    getBuildDataFilterFunction: () => {
      return buildGlobalDataFilterFunction(get, 'properties');
    },
  },
});

const useGlobalFilterStore = create<GlobalFilterSlice, [['zustand/persist', GlobalFilterSlice]]>(
  persist((set, get): GlobalFilterSlice => initalState(set, get), {
    name: 'global-filter-storage',
    storage: createJSONStorage(() => sessionStorage),
    merge: (persistedState, currentState) => merge(currentState, persistedState),
  })
);

export const clearGlobalFilterFromSessionStorage = () => {
  sessionStorage.removeItem('global-filter-storage');
};

export default useGlobalFilterStore;
